import {
  getCancelsOrderDailyCount,
  getCurrentOrderStatus,
  myOrderDetail,
  sellerCompletesOrder,
  sellerConfirmsToCreateOrder,
  sellerHasNotReceivedPayment,
  sellerRefusesToCreateOrder,
} from "@/apis/market";
import { useConfigContext } from "@/app";
import { INTERNET_DELAY, OrderStatus } from "@/constants";
import { StateReducer, useCreateReducer } from "@/libs/reducer";
import { useGlobalVerify } from "@/logic/verify";
import { useConfigStore } from "@/stores";
import { useCryptoMerchant } from "@/stores/CryptoTradingStore";
import {
  ErOrderIndicator,
  ErPayment,
  Order,
} from "@/types/Api/Market/Order/myOrderDetail";
import { useLatest, useLockFn, useMount, useRequest, useUnmount } from "ahooks";
import { Toast } from "antd-mobile";
import { useParams } from "react-router-dom";
import * as Sentry from '@sentry/react';
import { useEffect,useState,useRef } from "react";
/**
 * 使用 Turn Interface to Object 插件去直接成 pagestate
 */
interface PageState {
  /** 订单信息 */
  order?: Order;
  /** 卖家付款信息 */
  sellerPayment?: ErPayment;
  /** 买家付款信息 */
  buyerPayment?: ErPayment;
  /** 买家付款信息 */
  buyerOrderIndicator?: ErOrderIndicator;
  /** 订单倒计时 */
  countdown: number;
  /** 订单状态 */
  status: OrderStatus;
  /** 是否超时订单 */
  isTimeOut: boolean;

  /** 上传的转账凭证 */
  voucher: string;
}

const pagestate = {
  order: undefined,
  sellerPayment: undefined,
  buyerPayment: undefined,
  buyerOrderIndicator: undefined,
  countdown: 0,
  status: OrderStatus.BUYER_REQUEST,
  isTimeOut: false,
  voucher: "",
};

type ActionType =
  | "update-order"
  | "refresh-order"
  | "finish-countdown"
  | "update-status";

const stateReducer: StateReducer<PageState, ActionType> = (state, action) => {
  switch (action.type) {
    case "update-order":
      return Object.assign({}, state, {
        countdown: action.countdown,
        order: action.order,
        status: action.status as OrderStatus,
        isTimeOut: action.isTimeOut,
        sellerPayment: action.sellerPayment,
        buyerPayment: action.buyerPayment,
        buyerOrderIndicator: action.buyerOrderIndicator,
      });
    case "refresh-order":
      return Object.assign({}, state, {
        status: action.status as OrderStatus,
        isTimeOut: action.isTimeOut,
      });
    case "finish-countdown":
      return Object.assign({}, state, {
        countdown: 0,
      });
    case "update-status":
      return Object.assign({}, state, {
        status: action.status,
      });
    default:
      return state;
  }
};

export const useSetup = () => {
  const params = useParams();
  const { getSourcePath } = useConfigContext();

  const cryptoMerchantStore = useCryptoMerchant();
  const [verifySecurePassword, verifyGoogleAuthenticator] = useGlobalVerify();
  const [
    waitingSellerCompletesOrderDelayTime,
    waitingSellerConfirmDelayTime,
    buyerPaysOvertimeDelayTime,
    ProfessionBuyerPaysOvertimeDelayTime,
  ] = useConfigStore((state) => [
    state.waitingSellerCompletesOrderDelayTime,
    state.waitingSellerConfirmDelayTime,
    state.buyerPaysOvertimeDelayTime,
    state.ProfessionBuyerPaysOvertimeDelayTime,
  ]);
  const [state, dispatch] = useCreateReducer<PageState, ActionType>(
    stateReducer,
    pagestate,
    {}
  );
  const latestState = useLatest(state);
  const scollableRef = useRef<any>(null);
  const [ticking, setTicking] = useState<boolean>(false);
  const [isBottom, setIsBottom] = useState<boolean>(false);

  /* `loadStatus` 函数负责从服务器获取订单的当前状态。它使用“orderId”参数对“getCurrentOrderStatus”进行 API
  调用。如果API调用成功，则会更新state中的订单状态。如果订单存在争议，则会将状态更新为“OrderStatus.DISPUTING”。如果订单状态发生变化，它会调用“load”函数来获取更新后的订单详细信息。 */
  const loadStatus = async () => {
    try {
      const response = await getCurrentOrderStatus({
        orderId: Number(params.orderId),
      },{sellerType: cryptoMerchantStore.ApiPath});
      if (response.ok) {
        const oldStatus = latestState.current.status;
        const extend = {
          isTimeOut: latestState.current.isTimeOut,
          status: latestState.current.status,
        };
        // 争议订单
        if (latestState.current.order && latestState.current.order.appeal > 0) {
          latestState.current.status = OrderStatus.DISPUTING;
          if (response.data!.status === OrderStatus.TIME_OVER) {
            extend.isTimeOut = true;
          }
        } else {
          extend.status = response.data!.status;
        }

        dispatch({ type: "refresh-order", ...extend });

        if (extend.status !== oldStatus) {
          detail.run();
        }
      }
    } catch (error) {
      console.error(error);
      // TODO: 不上报错误，仅上报日志
      Sentry.captureMessage(
        `fn:CurrentOrderStatus, orderId: ${params.orderId}`,
        "info"
      );
    }
  };

  const { run, cancel } = useRequest(loadStatus, {
    pollingInterval: 10 * 1000,
    manual: true,
  });

  const refreshFun = ()=>{
    run();
    detail.run();
  }

  /* “load”函数负责从服务器获取订单详细信息。它以“orderId”作为参数，并调用 API
  来检索订单信息。如果API调用成功，它会使用获取的数据更新状态，包括订单详细信息、卖家付款信息、买家付款信息、倒计时、订单状态以及订单是否超时。如果 API 调用失败，则返回 false。 */
  const detail = useRequest(
    async () => {
      const response = await myOrderDetail({ orderId: Number(params.orderId) },{
        sellerType: cryptoMerchantStore.ApiPath
      });

      if (response.ok) {
        console.log('response.data!.currentStateExpireTime',response.data!.currentStateExpireTime);
        
        const serverTimestamp =
          Date.now() + Number(window.localStorage.getItem(INTERNET_DELAY));

        const extend = {
          countdown: 0,
          order: response.data!.order,
          sellerPayment: response.data!.sellerPayment,
          buyerPayment: response.data!.buyerPayment,
          status: response.data!.order.status as OrderStatus,
          isTimeOut: false,
          buyerOrderIndicator: response.data!.buyerOrderIndicator,
        };

        if (response.data!.currentStateExpireTime) {
          if (response.data!.currentStateExpireTime.expire) {
            extend.countdown = response.data!.currentStateExpireTime
              ? Number(response.data!.currentStateExpireTime.expire) * 1000 -
                serverTimestamp
              : 0;
          }
        }

        if (response.data!.order.appeal > 0) {
          extend.status = OrderStatus.DISPUTING;
          if (response.data!.order.status === OrderStatus.TIME_OVER) {
            extend.isTimeOut = true;
          }
        }

        // 提交状态
        await dispatch({
          type: "update-order",
          ...extend,
        });
      } else {
        // TODO: 处理请求失败
      }
    },
    {
      manual: true,
    }
  );

  const checkCancelable = useLockFn(async () => {
    const response = await getCancelsOrderDailyCount({},{
      sellerType: cryptoMerchantStore.ApiPath
    });
    if (response.ok) {
      return response.data!;
    } else {
      // TODO: 提示重试
    }
  });

  const confirmCancelTrading = useLockFn(async () => {
    const response = await sellerRefusesToCreateOrder({
      orderId: params.orderId!,
    },{sellerType:cryptoMerchantStore.ApiPath});
    if (response.ok) {
      dispatch({
        type: "update-status",
        status: OrderStatus.SELLER_CANCELED_DEAL,
      });
      Toast.show(response.msg);
      detail.run();
    } else {
      Toast.show(response.msg);
    }
    return response.ok;
  });

  const confirmHasNotRecived = useLockFn(async () => {
    const response = await sellerHasNotReceivedPayment({
      orderId: params.orderId!,
    },{sellerType:cryptoMerchantStore.ApiPath});
    if (response.ok) {
      dispatch({
        type: "update-status",
        status: OrderStatus.SELLER_DID_NOT_RECEIVE,
      });
      Toast.show(response.msg);
      detail.run();
    } else {
      Toast.show(response.msg);
    }
    return response.ok;
  });

  const confirmTrading = useLockFn(async () => {
    const response = await sellerConfirmsToCreateOrder({
      orderId: params.orderId!,
    },{
      sellerType:cryptoMerchantStore.ApiPath
    });
    if (response.ok) {
      dispatch({
        type: "update-status",
        status: OrderStatus.WAITING_FOR_TRANSFER,
      });
      Toast.show(response.msg);
      detail.run();
    } else {
      Toast.show(response.msg);
    }
    return response.ok;
  });

  const confirmCompleteTrading = useLockFn(async () => {
    const isVerified = await verifyGoogleAuthenticator();
    if (!isVerified) return;

    const code = await verifySecurePassword();
    if (!code) return;

    const response = await sellerCompletesOrder({
      orderId: params.orderId!,
      securePassword: code as string,
    },{
      sellerType:cryptoMerchantStore.ApiPath
    });
    if (response.ok) {
      dispatch({
        type: "update-status",
        status: OrderStatus.DEAL,
      });
      Toast.show(response.msg);
      detail.run();
    } else {
      Toast.show(response.msg);
    }
    return response.ok;
  });

  const handleScroll = (layout:any)=>{
    if (layout.scrollHeight - layout.scrollTop === layout.clientHeight) {
      console.log("滚动到底部");
      setIsBottom(true);
    }
  }
  const scrollFun = ()=>{
    if (!ticking) {
      window.requestAnimationFrame(() => {
        handleScroll(scollableRef.current);
        setTicking(false);
      });
      setTicking(true);
    }
  }

  useMount(() => {
    detail.runAsync().then(run);
  });
  useUnmount(() => {
    cancel();
    scollableRef.current && scollableRef.current.removeEventListener("scroll", scrollFun);//卸载滚动事件
  });

  useEffect(() => {
    if( [OrderStatus.BUYER_REQUEST,OrderStatus.WAITING_FOR_TRANSFER,OrderStatus.TRANSFERRED].includes(state.status) && scollableRef.current){ // 向下滚动监听
      scollableRef.current.addEventListener("scroll",scrollFun);
    }
  },[scollableRef.current])


  return {
    state,
    dispatch,
    orderDurations: {
      waitingSellerCompletesOrderDelayTime,
      waitingSellerConfirmDelayTime,
      buyerPaysOvertimeDelayTime,
      ProfessionBuyerPaysOvertimeDelayTime,
    },
    scollableRef,
    isBottom,
    getSourcePath,
    checkCancelable,
    confirmCancelTrading,
    confirmHasNotRecived,
    confirmTrading,
    confirmCompleteTrading,
    run,
    refreshFun,
  };
};
